import React from 'react';
import {
  DEVELOPMENT_FLAGS,
  isValidDevelopmentFlagKey,
  type DevelopmentFlagKey,
} from '@mentimeter/development-flags';
import { searchFlags, sortFlags } from '../utils';
import { DEFAULT_VALUE } from '../constants';
import type { FlagT } from '../types';
import { DevelopmentToolsContext } from '../context';
import type { DevelopmentToolsContextT } from '../context/DevelopmentToolsContext';

export function useDevelopmentFlags({
  searchString,
}: {
  searchString: string;
}) {
  const { overrides } = React.useContext(DevelopmentToolsContext);

  const developmentFlags = React.useMemo(
    () =>
      Object.keys(DEVELOPMENT_FLAGS)
        .filter(isValidDevelopmentFlagKey)
        .map(fromDevelopmentFlag(overrides))
        .sort(sortFlags)
        .filter(searchFlags(searchString)),
    [overrides, searchString],
  );

  const activeDevelopmentFlags = developmentFlags.filter((flag) => flag.active);

  return {
    developmentFlags,
    activeDevelopmentFlags,
    setDevelopmentFlag,
    resetDevelopmentFlag,
    resetAllDevelopmentFlags,
  };

  function setDevelopmentFlag(flagName: string, value: string) {
    overrides.set(flagName, value);
  }

  function resetDevelopmentFlag(flagName: string) {
    overrides.reset(flagName);
  }

  function resetAllDevelopmentFlags() {
    overrides.clearAll();
  }
}

function fromDevelopmentFlag(
  overrides: DevelopmentToolsContextT['overrides'],
): (flagKey: DevelopmentFlagKey) => FlagT {
  return (flagKey: DevelopmentFlagKey) => {
    const developmentFlag = DEVELOPMENT_FLAGS[flagKey];

    const flagValue = overrides.values[flagKey];

    return {
      key: flagKey,
      type: 'development',
      displayName: developmentFlag.displayName,
      description: developmentFlag.description,
      options: developmentFlag.values,
      value: flagValue ?? DEFAULT_VALUE,
      active: flagValue !== undefined,
    };
  };
}
